import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, resolveImg } from "../lib/api";
import Countdown from "./Countdown";
import { Gavel, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";

/** Live auction embed rendered inside blog content. Never a snapshot — always fetches. */
export default function AuctionEmbed({ listingId }) {
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/blog/auction-embed/${listingId}`)
      .then(r => setListing(r.data))
      .catch(() => setNotFound(true));
  }, [listingId]);

  if (notFound) return (
    <div className="my-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 text-sm text-slate-500">
      Referenced auction is no longer available.
    </div>
  );
  if (!listing) return <div className="my-6 h-32 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />;

  const ended = listing.status !== "live";
  const currentBid = listing.current_bid || listing.starting_bid;

  return (
    <Link to={`/auction/${listing.id}`} data-testid="blog-auction-embed"
      className="not-prose my-6 group grid grid-cols-1 sm:grid-cols-3 gap-0 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900 hover:shadow-xl transition">
      <div className="aspect-[4/3] sm:aspect-auto bg-slate-100 dark:bg-slate-800 overflow-hidden">
        {listing.photos?.[0] ? (
          <img src={resolveImg(listing.photos[0])} alt={listing.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="h-full w-full grid place-items-center text-slate-400"><Gavel className="h-8 w-8" /></div>
        )}
      </div>
      <div className="sm:col-span-2 p-4 md:p-5 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-600">Live Auction · {listing.category_name}</div>
        <div className="font-extrabold text-lg mt-1 leading-tight text-slate-900 dark:text-white">{listing.title}</div>
        <div className="text-xs text-slate-500 mt-1">by {listing.seller_name}</div>
        <div className="mt-3 flex flex-wrap items-baseline gap-4">
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">{ended ? "Final bid" : "Current bid"}</div>
            <div className="text-2xl font-extrabold">{inr(currentBid)}</div>
          </div>
          {!ended ? (
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Time left</div>
              <Countdown endsAt={listing.ends_at} />
            </div>
          ) : (
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] uppercase font-bold">
              {listing.status}
            </span>
          )}
        </div>
        <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-amber-600 self-start">
          {ended ? "View auction" : "View & Bid"} <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}

/** Parse blog HTML for `[[auction:<id>]]` tokens and render live embeds inline. */
export function renderBlogWithEmbeds(html) {
  if (!html) return null;
  const parts = [];
  const re = /\[\[auction:([a-f0-9-]{8,})\]\]/g;
  let last = 0, m;
  while ((m = re.exec(html)) !== null) {
    if (m.index > last) parts.push({ type: "html", value: html.slice(last, m.index) });
    parts.push({ type: "embed", id: m[1] });
    last = m.index + m[0].length;
  }
  if (last < html.length) parts.push({ type: "html", value: html.slice(last) });
  return parts.map((p, i) =>
    p.type === "html"
      ? <div key={`html-${i}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.value) }} />
      : <AuctionEmbed key={`embed-${p.id}-${i}`} listingId={p.id} />
  );
}
